import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Button, Stack } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { useShipmentMethods } from '@features/shipments/hooks/useShipmentMethods';
import CreateShipmentMethodDialog from '@features/shipments/components/methods/CreateShipmentMethodDialog';
import EditShipmentMethodDialog from '@features/shipments/components/methods/EditShipmentMethodDialog';
import { Icon } from '@shared/icons';

interface ShippingMethodSelectorProps {
  selectedMethod?: ShipmentMethod;
  onMethodChange: (method: ShipmentMethod) => void;
  onMethodCreate: (method: ShipmentMethod) => void;
  showEditButton?: boolean;
}

const ShippingMethodSelector: React.FC<ShippingMethodSelectorProps> = ({
  selectedMethod: initialSelectedMethod,
  onMethodChange,
  onMethodCreate,
  showEditButton = true
}) => {

  const [selectedMethod, setSelectedMethod] = useState(initialSelectedMethod);
  useEffect(() => {
    setSelectedMethod(initialSelectedMethod);
  }, [initialSelectedMethod]);
  const { shipmentMethods, createShipmentMethod } = useShipmentMethods();
  const [shipmentOptionsRowOpen, setShipmentOptionsRowOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleCreateShipmentMethod = async (newMethod: any) => {
    const result = await createShipmentMethod({
      transportMethod: newMethod.transportMethod,
      duration: newMethod.duration,
      name: newMethod.name,
    });

    onMethodCreate(result);
    setSelectedMethod(result);
    setShipmentOptionsRowOpen(false);
    setCreateDialogOpen(false);
  };

  const handleShipmentMethodChange = (method: ShipmentMethod) => {
    onMethodChange(method);
    setSelectedMethod(method);
    setShipmentOptionsRowOpen(false);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', px: 3, height: 40 }}>
        <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-start" flex={1}>
          <Typography variant="body2" sx={{ width: '100px' }}>Shipping Time</Typography>

          {selectedMethod ? (
            <Chip
              color="secondary"
              label={selectedMethod.name ? `${selectedMethod.name}` : `${selectedMethod.transportMethod}`}
              icon={shipmentOptionsRowOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              onClick={() => setShipmentOptionsRowOpen(!shipmentOptionsRowOpen)}
            />
          ) : (
            <Chip
              onClick={() => setShipmentOptionsRowOpen(!shipmentOptionsRowOpen)}
              variant="outlined"
              label="Add shipment method"
              color="secondary"
              sx={{ cursor: 'pointer' }}
              icon={shipmentOptionsRowOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            />
          )}
        </Stack>

        {showEditButton && selectedMethod && (
          <Button
            onClick={() => setEditDialogOpen(true)}
            endIcon={<Icon icon="IcEdit" className="edit-icon" />}
            sx={{
              '.edit-icon': {
                display: 'none',
              },
              '&:hover .edit-icon': {
                display: 'block'
              }
            }}
            variant="text"
            color="secondary"
          >
            <Typography variant="body2">
              {(selectedMethod?.duration || 0) + ' days'}
            </Typography>
          </Button>
        )}
      </Box>

      {shipmentOptionsRowOpen && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', overflowX: 'auto', gap: 1, alignItems: 'center', pb: 2 }}>
            {shipmentMethods?.map(method => (
              <Chip
                key={method.id}
                onClick={() => handleShipmentMethodChange(method)}
                label={method.name ? `${method.name} (${method.duration} days)` : `${method.transportMethod} (${method.duration} days)`}
                color="secondary"
                sx={{ cursor: 'pointer' }}
              />
            ))}

            <Chip
              key="default"
              variant="outlined"
              icon={<AddIcon fontSize="small" />}
              onClick={() => setCreateDialogOpen(true)}
              label="Create New"
              color="secondary"
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
      )}

      <CreateShipmentMethodDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateShipmentMethod}
      />

      <EditShipmentMethodDialog
        shipmentMethod={selectedMethod}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onFinish={handleShipmentMethodChange}
      />
    </>
  );
};

export default ShippingMethodSelector;
